import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { MOBILE_BREAKPOINT } from "../config";
import styled, { css } from "../styles";
import { Card } from "./Card";
import { CloseIcon } from "./CloseIcon";

interface ModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  setIsOpen?: (arg0: boolean) => void;
  onClose?: () => void;
  maxWidth?: number;
  noPadding?: boolean;
  zIndex?: number;
  overflow?: "visible";
}

export function Modal({
  isOpen,
  setIsOpen,
  children,
  onClose,
  maxWidth,
  noPadding,
  zIndex = 999,
  overflow,
}: ModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog
          static
          open={isOpen}
          onClose={() => {
            setIsOpen && setIsOpen(false);
            onClose && onClose();
          }}
        >
          <Backdrop
            as={motion.div}
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(3px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
            aria-hidden="true"
            $zIndex={zIndex - 1}
          />

          <CenteredWrapper $zIndex={zIndex}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
            >
              <ModalWrapper>
                {setIsOpen ? (
                  <CloseButtonWrap onClick={() => setIsOpen(false)}>
                    <CloseWrap>
                      <CloseIcon width={18} height={18} />
                    </CloseWrap>
                  </CloseButtonWrap>
                ) : null}
                <CenteredContainer
                  $overflow={overflow}
                  maxWidth={maxWidth}
                  noPadding={noPadding}
                >
                  <Dialog.Panel style={{ width: "100%", height: "100%" }}>
                    <ScrollWrapper $overflow={overflow}>
                      {children}
                    </ScrollWrapper>
                  </Dialog.Panel>
                </CenteredContainer>
              </ModalWrapper>
            </motion.div>
          </CenteredWrapper>
        </Dialog>
      )}
    </AnimatePresence>
  );
}

const CloseWrap = styled.div`
  background-color: ${(p) => p.theme.color.card.background};
  border-radius: 999px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${(p) => p.theme.shadow.imageFloatingIcon};
  transition: all 0.15s cubic-bezier(0.32, 0.72, 0, 1);
  transform: scale(1);

  &:hover {
    filter: brightness(0.95);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const CenteredContainer = styled(Card)<{
  maxWidth?: number;
  noPadding?: boolean;
  $overflow?: "visible";
}>`
  display: flex;
  align-items: flex-start;
  width: ${(p) => (p.maxWidth ? p.maxWidth : 600)}px;
  max-width: 100%;
  min-height: 120px;
  position: relative;
  outline: none !important;

  [data-headlessui-state="open"] *:focus {
    outline: none !important;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    min-height: auto;
  }

  ${(p) =>
    p.$overflow &&
    css`
      overflow: visible;
    `}

  ${(p) =>
    p.noPadding
      ? css`
          padding: 0;
        `
      : css`
          padding: ${(p) => p.theme.spacing.l};

          @media (max-width: ${MOBILE_BREAKPOINT}px) {
            padding: ${(p) => p.theme.spacing.m};
          }
        `}
`;

const Backdrop = styled.div<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${(p) => p.$zIndex};

  @media (prefers-reduced-motion: reduce) {
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }
`;

const CenteredWrapper = styled.div<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${(p) => p.$zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(p) => p.theme.spacing.m};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${(p) => p.theme.spacing.s};
    align-items: safe center;
    /* Fallback for browsers that don't support 'safe' */
    @supports not (align-items: safe center) {
      align-items: flex-start;
      padding-top: ${(p) => p.theme.spacing.l};
    }
  }
`;

const ModalWrapper = styled.div`
  position: relative;
  max-width: 100%;
`;

const CloseButtonWrap = styled.div`
  position: absolute;
  top: -16px;
  right: -16px;
  cursor: pointer;
  z-index: 10;

  @media (max-width: 1080px) {
    top: -8px;
    right: -8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    top: ${(p) => p.theme.spacing.xs};
    right: ${(p) => p.theme.spacing.xs};
    position: fixed;
  }

  svg {
    path {
      fill: ${(p) => p.theme.color.typography.secondary};
    }
  }
`;

const ScrollWrapper = styled.div<{
  $overflow?: "visible";
}>`
  width: 100%;
  height: 100%;
  max-height: 85vh;
  overflow-y: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-height: 90vh;
  }

  ${(p) =>
    p.$overflow &&
    css`
      overflow: visible;
    `}
`;
